import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

interface BillingInfo {
  planTier: 'trial' | 'professional' | 'enterprise';
  stripeSubscriptionStatus: string | null;
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
  trialExpired: boolean;
  trialDays: number;
  hasStripeCustomer: boolean;
  hasActiveSubscription: boolean;
  analysesUsed: number;
  analysisLimit: number | null;
}

const PLANS = {
  professional: {
    name: 'Professional',
    monthly: 299,
    annual: 2990,
    annualSavings: 598,
    analyses: 'Unlimited',
    features: [
      'Unlimited impact analyses',
      'Branded PDF exports',
      'Community Health dashboard',
      'Industry Target scoring',
      'Workforce Profile',
      'AI narrative summaries',
      'Data citations included',
    ],
    color: 'blue',
    monthlyPriceId: import.meta.env.VITE_STRIPE_PRICE_ID_PRO ?? '',
    annualPriceId: import.meta.env.VITE_STRIPE_PRICE_ID_PRO_ANNUAL ?? '',
  },
  enterprise: {
    name: 'Enterprise',
    monthly: 599,
    annual: 5990,
    annualSavings: 1198,
    analyses: 'Unlimited',
    features: [
      'Everything in Professional',
      'Multiple jurisdictions',
      'SAML / Single Sign-On',
      'Dedicated support + SLA',
      'Custom RIMS data integration',
      'Usage reporting',
    ],
    color: 'purple',
    monthlyPriceId: import.meta.env.VITE_STRIPE_PRICE_ID_ENTERPRISE ?? '',
    annualPriceId: import.meta.env.VITE_STRIPE_PRICE_ID_ENTERPRISE_ANNUAL ?? '',
  },
} as const;

export function SettingsBillingRoute() {
  const getToken = useSessionTokenGetter();
  const [info, setInfo] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [annual, setAnnual] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [startingSub, setStartingSub] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/settings/billing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok && !cancelled) setInfo((await res.json()) as BillingInfo);
      if (!cancelled) setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const handleUpgrade = async (planKey: 'professional' | 'enterprise') => {
    const priceId = annual ? PLANS[planKey].annualPriceId : PLANS[planKey].monthlyPriceId;
    if (!priceId) return;
    setCheckoutLoading(planKey);
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/billing/checkout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ priceId }),
    });
    if (res.ok) {
      const { url } = (await res.json()) as { url: string };
      window.location.href = url;
    }
    setCheckoutLoading(null);
  };

  // End the trial now: charges the card on file and unlocks unlimited.
  const handleStartSubscription = async () => {
    setStartingSub(true);
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/billing/end-trial`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      window.location.reload();
      return;
    }
    setStartingSub(false);
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/billing/portal`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const { url } = (await res.json()) as { url: string };
      window.location.href = url;
    }
    setPortalLoading(false);
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="h-6 w-40 animate-pulse rounded bg-slate-200" />
      </div>
    );
  }

  const plan = info?.planTier ?? 'trial';
  const isTrial = plan === 'trial';
  const isPro = plan === 'professional';
  const isEnterprise = plan === 'enterprise';
  // In the card-required model every org that can reach this page already has a
  // subscription (trialing or active). A trialing org converts via end-trial,
  // not a fresh checkout — so the checkout plan cards only apply to the (rare)
  // no-subscription case.
  const hasSubscription = ['trialing', 'active', 'past_due'].includes(
    info?.stripeSubscriptionStatus ?? '',
  );
  const isTrialing = info?.stripeSubscriptionStatus === 'trialing';

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 space-y-8">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Billing &amp; Plan</h1>
        <p className="mt-1 text-sm text-slate-500">
          Manage your subscription. All plans include full platform access.
        </p>
      </div>

      {/* Trial status card */}
      {isTrial && (
        <div
          className={`rounded-lg border p-6 ${info?.trialExpired ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}
        >
          <div className="flex items-start justify-between">
            <div>
              <p
                className={`text-sm font-semibold ${info?.trialExpired ? 'text-red-800' : 'text-amber-800'}`}
              >
                {info?.trialExpired ? 'Trial expired' : 'Free trial'}
              </p>
              {info?.trialExpired ? (
                <p className="mt-1 text-sm text-red-700">
                  Your trial has ended. Choose a plan below to continue.
                </p>
              ) : (
                <p className="mt-1 text-sm text-amber-700">
                  {info?.trialDaysRemaining != null ? (
                    <>
                      <span className="font-semibold">{info.trialDaysRemaining} days</span>{' '}
                      remaining ·{' '}
                    </>
                  ) : null}
                  {info?.analysesUsed ?? 0} of {info?.analysisLimit ?? 1} trial analyses used
                </p>
              )}
            </div>
            {!info?.trialExpired && info?.analysisLimit != null && (
              <div className="ml-6 shrink-0 w-24">
                <div className="h-1.5 w-full rounded-full bg-amber-200">
                  <div
                    className="h-1.5 rounded-full bg-amber-500 transition-all"
                    style={{
                      width: `${Math.min(100, ((info.analysesUsed ?? 0) / info.analysisLimit) * 100)}%`,
                    }}
                  />
                </div>
                <p className="mt-1 text-right text-xs text-amber-600">
                  {info.analysesUsed}/{info.analysisLimit} runs
                </p>
              </div>
            )}
          </div>
          {isTrialing && (
            <div className="mt-4 border-t border-amber-200 pt-4">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => void handleStartSubscription()}
                  disabled={startingSub}
                  className="rounded-lg bg-develio-teal px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                >
                  {startingSub ? 'Starting…' : 'Start subscription now'}
                </button>
                <button
                  type="button"
                  onClick={() => void handlePortal()}
                  disabled={portalLoading}
                  className="rounded-lg border border-amber-300 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                >
                  {portalLoading ? 'Loading…' : 'Manage or cancel'}
                </button>
              </div>
              <p className="mt-2 text-xs text-amber-700">
                Start now ends your trial today, charges your card on file, and unlocks unlimited
                analyses. Otherwise your subscription starts automatically when the trial ends on{' '}
                {info?.trialEndsAt
                  ? new Date(info.trialEndsAt).toLocaleDateString()
                  : 'your trial end date'}
                . Cancel anytime before then to avoid charges.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Active subscription card */}
      {!isTrial && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Current plan</p>
            <p className="mt-1 text-lg font-semibold text-slate-900 capitalize">{plan}</p>
            {info?.stripeSubscriptionStatus && (
              <p className="text-xs text-slate-400 capitalize mt-0.5">
                {info.stripeSubscriptionStatus.replace(/_/g, ' ')}
              </p>
            )}
          </div>
          {info?.hasActiveSubscription && (
            <button
              type="button"
              onClick={() => void handlePortal()}
              disabled={portalLoading}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {portalLoading ? 'Loading…' : 'Manage subscription'}
            </button>
          )}
        </div>
      )}

      {/* Plan comparison via checkout — only for orgs without a subscription.
          Trialing/active orgs convert via end-trial or manage via the portal,
          never a second checkout. */}
      {!isEnterprise && !hasSubscription && (
        <>
          {/* Billing period toggle */}
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setAnnual(false)}
              className={`text-sm font-medium ${!annual ? 'text-slate-900' : 'text-slate-400'}`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setAnnual((a) => !a)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${annual ? 'bg-develio-teal' : 'bg-slate-200'}`}
              aria-label="Toggle annual billing"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${annual ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
            <button
              type="button"
              onClick={() => setAnnual(true)}
              className={`text-sm font-medium ${annual ? 'text-slate-900' : 'text-slate-400'}`}
            >
              Annual
              <span className="ml-1.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">
                2 months free
              </span>
            </button>
          </div>

          {/* Plan cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Professional */}
            <div
              className={`rounded-xl border-2 p-6 ${isPro ? 'border-blue-400 bg-blue-50' : 'border-blue-200 bg-white'}`}
            >
              {isPro && (
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-600">
                  Current plan
                </p>
              )}
              <p className="text-lg font-bold text-slate-900">Professional</p>
              <div className="mt-2">
                <span className="text-3xl font-bold text-slate-900">
                  ${annual ? '2,990' : '299'}
                </span>
                <span className="text-sm text-slate-500">/{annual ? 'year' : 'mo'}</span>
                {annual && (
                  <p className="text-xs text-emerald-600 font-medium mt-0.5">
                    Save ${PLANS.professional.annualSavings}/year
                  </p>
                )}
              </div>
              <ul className="mt-4 space-y-1.5">
                {PLANS.professional.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="mt-0.5 text-blue-500">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              {!isPro && (
                <button
                  type="button"
                  onClick={() => void handleUpgrade('professional')}
                  disabled={checkoutLoading !== null}
                  className="mt-5 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {checkoutLoading === 'professional' ? 'Redirecting…' : 'Start free trial →'}
                </button>
              )}
            </div>

            {/* Enterprise */}
            <div className="rounded-xl border-2 border-purple-200 bg-white p-6">
              <p className="text-lg font-bold text-slate-900">Enterprise</p>
              <div className="mt-2">
                <span className="text-3xl font-bold text-slate-900">
                  ${annual ? '5,990' : '599'}
                </span>
                <span className="text-sm text-slate-500">/{annual ? 'year' : 'mo'}</span>
                {annual && (
                  <p className="text-xs text-emerald-600 font-medium mt-0.5">
                    Save ${PLANS.enterprise.annualSavings}/year
                  </p>
                )}
              </div>
              <ul className="mt-4 space-y-1.5">
                {PLANS.enterprise.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="mt-0.5 text-purple-500">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => void handleUpgrade('enterprise')}
                disabled={checkoutLoading !== null}
                className="mt-5 w-full rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {checkoutLoading === 'enterprise' ? 'Redirecting…' : 'Get started →'}
              </button>
            </div>
          </div>

          <p className="text-center text-xs text-slate-400">
            Both plans are dramatically less than the $6,500+/year alternatives. Cancel anytime.
          </p>
        </>
      )}

      {/* Enterprise — upgrade to annual or manage */}
      {isEnterprise && (
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-5 text-sm text-purple-800">
          You&apos;re on the Enterprise plan. Contact{' '}
          <a href="mailto:sales@develio.com" className="underline">
            sales@develio.com
          </a>{' '}
          for custom contracts, additional jurisdictions, or SLA questions.
        </div>
      )}
    </div>
  );
}
